import type {ChangeEvent} from 'react';
import {downloadBlobAsFile} from '@Utils/file.util';
import type {
  AgentOverrideRow,
  ForcedImageProvider,
  ForcedProviderOption,
  ForcedTextRemovalMethod,
  ForcedTextRemovalMethodOption,
  OverrideSelectHandlerOptions,
  OverrideSelectOption,
} from './ai-chat-super-user-options.types';
import {NO_OVERRIDE_VALUE} from './ai-chat-super-user-options.types';

const BASE = 'ai';

const FORCED_IMAGE_PROVIDERS: readonly ForcedImageProvider[] = [NO_OVERRIDE_VALUE, 'gemini-3.1-flash-image-preview', 'openai-gpt-image', 'seedream'];

const FORCED_TEXT_REMOVAL_METHODS: readonly ForcedTextRemovalMethod[] = [
  NO_OVERRIDE_VALUE,
  'nanoBanana',
  'gptImage2',
  'wanImageEdit',
  'briaErase',
  'flux2Klein9b',
  'seedream5Pro',
  'jasper',
];

const TEXT_REMOVAL_METHOD_LABELS: Record<Exclude<ForcedTextRemovalMethod, typeof NO_OVERRIDE_VALUE>, string> = {
  nanoBanana: 'Nano Banana',
  gptImage2: 'GPT Image 2',
  wanImageEdit: 'Wan 2.7 Image Edit',
  briaErase: 'Bria Eraser',
  flux2Klein9b: 'FLUX.2 Klein 9B',
  seedream5Pro: 'Seedream 5.0 Pro',
  jasper: 'Jasper',
};

export const IMAGE_PROVIDER_LABELS: Record<Exclude<ForcedImageProvider, typeof NO_OVERRIDE_VALUE>, string> = {
  'gemini-3.1-flash-image-preview': 'Nano Banana 2',
  'openai-gpt-image': 'GPT Image 2',
  seedream: 'Seedream',
};

// The retired preview id only ever appears on chat messages generated before the GA cutover.
const GEMINI_MODEL_VERSION_LABELS: Record<string, string> = {
  'gemini-3.1-flash-image': 'Nano Banana 2',
  'gemini-3.1-flash-image-preview': 'Nano Banana 2 (preview)',
};

export function getImageProviderLabel(provider: string, model?: string | null): string {
  if (model && model in GEMINI_MODEL_VERSION_LABELS) {
    return GEMINI_MODEL_VERSION_LABELS[model];
  }
  if (!isForcedImageProvider(provider) || provider === NO_OVERRIDE_VALUE) {
    return provider;
  }
  return IMAGE_PROVIDER_LABELS[provider];
}

export function getInitialAgents(): AgentOverrideRow[] {
  const raw: unknown = window.reactPageProps?.aiChatSuperUserVariantOverrides;
  return Array.isArray(raw) ? (raw as AgentOverrideRow[]) : [];
}

export function setSuperUserVariantOverride(agentType: string, alias: string): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserVariantOverride', {agentType, alias}) as Promise<void>;
}

export function clearSuperUserVariantOverrides(): Promise<void> {
  return window.PMW.writeLocal(BASE + '/clearSuperUserVariantOverrides', {}) as Promise<void>;
}

export async function downloadUserAttachments(chatId: string): Promise<void> {
  const params = new URLSearchParams({chatId});
  const url = `${window.PMW.util.site_url(BASE + '/superUserDownloadAllChatAttachments')}?${params.toString()}`;

  const res = await fetch(url, {credentials: 'same-origin'});
  if (!res.ok || isJsonResponse(res)) {
    throw await readSuperUserDownloadError(res);
  }

  const blob = await res.blob();
  downloadBlobAsFile(blob, `aichat-attachments-${chatId}.zip`);
}

function isJsonResponse(res: Response): boolean {
  const contentType = res.headers.get('Content-Type') ?? '';
  return contentType.toLowerCase().includes('application/json');
}

async function readSuperUserDownloadError(res: Response): Promise<Error> {
  const fallback = new Error(`Download failed: ${res.status}`);
  try {
    const body = (await res.json()) as {message?: string; status?: string};
    return body?.message ? new Error(body.message) : fallback;
  } catch {
    return fallback;
  }
}

/** Page props are attacker-shaped `unknown`, and a stored override the union no longer contains must degrade to "no override" rather than select a value the backend would reject. */
function readForcedOptionFromPageProps<TValue extends string>(rawValue: unknown, allowedValues: readonly TValue[], noOverrideValue: TValue): TValue {
  if (typeof rawValue !== 'string') {
    return noOverrideValue;
  }
  return allowedValues.find((allowedValue) => allowedValue === rawValue) ?? noOverrideValue;
}

/** Every label map deliberately excludes `NO_OVERRIDE_VALUE` (its `Record<Exclude<…>>` type makes the rest exhaustive), so the unlabelled value is always the no-override entry and gets the translated "auto" label. */
function buildOverrideOptions<TValue extends string>(values: readonly TValue[], labelsByValue: Partial<Record<TValue, string>>): OverrideSelectOption<TValue>[] {
  return values.map((value) => ({
    value,
    label: labelsByValue[value] ?? window.i18next.t('pmwjs_ai_chat_provider_auto'),
  }));
}

/** Builds the `<select>` onChange for one forced-override row: ignore values outside the union, then persist before mirroring into local state so a failed save never shows as applied. */
export function createOverrideSelectHandler<TValue extends string>(options: OverrideSelectHandlerOptions<TValue>): (event: ChangeEvent<HTMLSelectElement>) => void {
  return (event: ChangeEvent<HTMLSelectElement>): void => {
    const value = event.target.value;
    if (!options.isValidValue(value)) {
      return;
    }
    void options.save(value).then(
      () => {
        options.onSaved(value);
      },
      () => {
        options.onError();
      }
    );
  };
}

export function isForcedImageProvider(value: string): value is ForcedImageProvider {
  return FORCED_IMAGE_PROVIDERS.some((provider) => provider === value);
}

export function getInitialForcedImageProvider(): ForcedImageProvider {
  return readForcedOptionFromPageProps(window.reactPageProps?.aiChatForcedImageProvider, FORCED_IMAGE_PROVIDERS, NO_OVERRIDE_VALUE);
}

export function setSuperUserForcedImageProvider(provider: ForcedImageProvider): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedImageProvider', {provider}) as Promise<void>;
}

export function getForcedImageProviderOptions(): ForcedProviderOption[] {
  return buildOverrideOptions(FORCED_IMAGE_PROVIDERS, IMAGE_PROVIDER_LABELS);
}

export function isForcedTextRemovalMethod(value: string): value is ForcedTextRemovalMethod {
  return FORCED_TEXT_REMOVAL_METHODS.some((method) => method === value);
}

export function getInitialForcedTextRemovalMethod(): ForcedTextRemovalMethod {
  return readForcedOptionFromPageProps(window.reactPageProps?.mteForcedTextRemovalMethod, FORCED_TEXT_REMOVAL_METHODS, NO_OVERRIDE_VALUE);
}

export function setSuperUserForcedTextRemovalMethod(method: ForcedTextRemovalMethod): Promise<void> {
  return window.PMW.writeLocal(BASE + '/setSuperUserForcedTextRemovalMethod', {method}) as Promise<void>;
}

export function getForcedTextRemovalMethodOptions(): ForcedTextRemovalMethodOption[] {
  return buildOverrideOptions(FORCED_TEXT_REMOVAL_METHODS, TEXT_REMOVAL_METHOD_LABELS);
}
